/**
 * Export Table Data for Human Loss Reports
 */

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import {
  transformPrimaryReportData,
  transformDetailedReportData,
  transformMISDistrictReportData,
  getTimestamp
} from './excelUtilsHumanLoss';

/**
 * Extract human_loss_report array from various response structures
 */
const extractHumanLossData = (rawData) => {
  console.log('🔍 Extracting human loss data for Excel export:', rawData);
  
  if (Array.isArray(rawData)) {
    console.log('✅ Found direct array with', rawData.length, 'records');
    return rawData;
  }
  
  if (rawData?.human_loss_report && Array.isArray(rawData.human_loss_report)) {
    console.log('✅ Found human_loss_report array with', rawData.human_loss_report.length, 'records');
    return rawData.human_loss_report;
  }
  
  if (rawData?.data?.human_loss_report && Array.isArray(rawData.data.human_loss_report)) {
    console.log('✅ Found data.human_loss_report array with', rawData.data.human_loss_report.length, 'records');
    return rawData.data.human_loss_report;
  }
  
  if (rawData?.data && Array.isArray(rawData.data)) {
    console.log('✅ Found data array with', rawData.data.length, 'records');
    return rawData.data;
  }
  
  console.error('⚠️ No array found in data structure');
  return [];
};

/**
 * Generate PRIMARY Human Loss Excel Report
 * ✅ FIXED: No background color, proper NA handling, no trailing zeros
 */
export const generateHumanLossPrimaryExcel = async (config) => {
  const { data: rawData, filename } = config;
  console.log('📊 generateHumanLossPrimaryExcel called');

  const data = extractHumanLossData(rawData);
  console.log('- Extracted records:', data?.length || 0);

  if (!data || data.length === 0) {
    alert('No data available to export');
    return;
  }

  try {
    const { columns, transformedData } = transformPrimaryReportData(data);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Human Loss Primary Report');

    // Row 1: Headers
    const headerRow = worksheet.addRow(columns.map(col => col.label));

    // ✅ FIXED: Bold headers WITHOUT background color
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      // ❌ REMOVED: Background color
    });

    // Row 2+: Data rows
    transformedData.forEach((row) => {
      const rowData = columns.map(col => row[col.label]);
      const dataRow = worksheet.addRow(rowData);

      // ✅ FIXED: Preserve NA, remove trailing zeros
      dataRow.eachCell((cell, cellIdx) => {
        const colLabel = columns[cellIdx - 1].label;
        const value = cell.value;
        
        cell.alignment = { horizontal: 'left', vertical: 'middle' };

        // ✅ FIXED: Keep 'NA' as text, format numbers properly
        if (value === null || value === undefined) {
  cell.value = '';        // true blank
} else if (value === '') {
  cell.value = '';        // keep empty as empty
} else if (value === 'NA') {
  cell.value = 'NA';   
        } else if (colLabel === 'Latitude' || colLabel === 'Longitude') {
          // Convert to number without trailing zeros
          const numValue = parseFloat(value);
          if (!isNaN(numValue)) {
            cell.value = numValue;
            cell.numFmt = '0.######';  // ✅ Up to 6 decimals, no trailing zeros
          } else {
            cell.value = 'NA';
          }
        } else if (colLabel === 'Number of deaths' || colLabel === 'Number of injured') {
          const numValue = parseInt(value);
          cell.value = !isNaN(numValue) ? numValue : 0;
          cell.numFmt = '0';  // Integer format
        }
      });
    });

    // Auto-adjust column widths
    columns.forEach((col, index) => {
      worksheet.getColumn(index + 1).width = 18;
    });

    // Set row height for header
    worksheet.getRow(1).height = 25;

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const finalFilename = `Human_loss_primary_report_${getTimestamp()}.xlsx`;
    saveAs(blob, finalFilename);
    console.log(`✅ Primary Excel downloaded: ${finalFilename}`);
  } catch (error) {
    console.error('❌ Primary Excel generation failed:', error);
    alert(`Excel Generation Error: ${error.message}`);
  }
};

/**
 * Generate DETAILED Human Loss Excel Report
 * ✅ FIXED: No background color, proper NA handling, no trailing zeros
 */
export const generateHumanLossDetailedExcel = async (config) => {
  const { data: rawData, filename } = config;
  console.log('📊 generateHumanLossDetailedExcel called');

  const data = extractHumanLossData(rawData);
  console.log('- Extracted records:', data?.length || 0);

  if (!data || data.length === 0) {
    alert('No data available to export');
    return;
  }

  try {
    const { columns, transformedData } = transformDetailedReportData(data);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Human Loss Detailed Report');

    // Row 1: Headers
    const headerRow = worksheet.addRow(columns.map(col => col.label));

    // ✅ FIXED: Bold headers WITHOUT background color
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      // ❌ REMOVED: Background color
    });

    // Row 2+: Data rows
// Row 2+: Data rows
transformedData.forEach(row => {
  const rowData = columns.map(col => row[col.label]);
  const dataRow = worksheet.addRow(rowData);

  // ✅ FIXED: Keep truly empty cells as BLANK (not "NA")
  dataRow.eachCell((cell, cellIdx) => {
    const colLabel = columns[cellIdx - 1].label;
    const value = cell.value;
    cell.alignment = { horizontal: 'left', vertical: 'middle' };

    // ✅ IMPORTANT: Only convert EXPLICIT 'NA' string, keep empty as empty
    if (value === null || value === undefined) {
      cell.value = '';        // true blank
    } else if (value === '') {
      cell.value = '';        // keep empty as empty
    } else if (String(value).trim() === 'NA') {
      cell.value = 'NA';      // explicit NA stays
    } else if (colLabel === 'Latitude' || colLabel === 'Longitude') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        cell.value = numValue;
        cell.numFmt = '0.######';
      } else {
        cell.value = '';      // ❌ NOT 'NA' for empty lat/long
      }
    } else if (colLabel === 'S.NO.' || colLabel === 'Age') {
      const numValue = parseInt(value);
      if (!isNaN(numValue)) {
        cell.value = numValue;
        cell.numFmt = '0';
      } else {
        cell.value = '';      // ❌ NOT 'NA' for empty age
      }
    }
  });
});


    // Auto-adjust column widths
    columns.forEach((col, index) => {
      const width = col.label.length > 20 ? 25 : 18;
      worksheet.getColumn(index + 1).width = width;
    });

    // Set row height for header
    worksheet.getRow(1).height = 30;

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const finalFilename = `Human_loss_detailed_report_${getTimestamp()}.xlsx`;
    saveAs(blob, finalFilename);
    console.log(`✅ Detailed Excel downloaded: ${finalFilename}`);
  } catch (error) {
    console.error('❌ Detailed Excel generation failed:', error);
    alert(`Excel Generation Error: ${error.message}`);
  }
};

/**
 * Generate MIS Human Loss Excel Report
 * ✅ FIXED: No background color on data headers, proper NA handling
 */
export const generateHumanLossMISExcel = async (config) => {
  const { data: rawData, fromDate, toDate, filename } = config;
  console.log('📊 generateHumanLossMISExcel called');

  const data = extractHumanLossData(rawData);
  console.log('- Extracted records:', data?.length || 0);

  if (!data || data.length === 0) {
    alert('No data available to export');
    return;
  }

  try {
    const { columns, transformedData } = transformMISDistrictReportData(data);
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Sheet 1');

    // Row 1: Report Name
    const metadataRow1 = worksheet.addRow(['Report Name: Human Loss MIS Report']);
    metadataRow1.getCell(1).font = { bold: true };
    metadataRow1.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(`A1:${String.fromCharCode(64 + columns.length)}1`);

    // Row 2: From Date
    const metadataRow2 = worksheet.addRow([`From Date: ${fromDate || 'N/A'}`]);
    metadataRow2.getCell(1).font = { bold: true };
    metadataRow2.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(`A2:${String.fromCharCode(64 + columns.length)}2`);

    // Row 3: To Date
    const metadataRow3 = worksheet.addRow([`To Date: ${toDate || 'N/A'}`]);
    metadataRow3.getCell(1).font = { bold: true };
    metadataRow3.getCell(1).alignment = { horizontal: 'left', vertical: 'middle' };
    worksheet.mergeCells(`A3:${String.fromCharCode(64 + columns.length)}3`);

    // Row 4: Headers
    const headerRow = worksheet.addRow(columns.map(col => col.label));
    
    // ✅ FIXED: Bold headers WITHOUT background color
    headerRow.eachCell((cell) => {
      cell.font = { bold: true };
      cell.alignment = { horizontal: 'left', vertical: 'middle', wrapText: true };
      // ❌ REMOVED: Background color
    });

// Row 5+: Data rows
transformedData.forEach(row => {
  const rowData = columns.map(col => row[col.label]);
  const dataRow = worksheet.addRow(rowData);

  // ✅ FIXED: Preserve blanks, only explicit NA stays NA
  dataRow.eachCell((cell, cellIdx) => {
    const colLabel = columns[cellIdx - 1].label;
    const value = cell.value;
    cell.alignment = { horizontal: 'left', vertical: 'middle' };

    if (value === null || value === undefined) {
      cell.value = '';        // true blank
    } else if (value === '') {
      cell.value = '';        // keep empty as empty
    } else if (value === 'NA') {
      cell.value = 'NA';      // explicit NA stays
    } else if (colLabel === 'Latitude' || colLabel === 'Longitude') {
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        cell.value = numValue;
        cell.numFmt = '0.######';
      } else {
        cell.value = '';
      }
    } else if (colLabel === 'S.NO.' || colLabel === 'Age') {
      const numValue = parseInt(value);
      cell.value = !isNaN(numValue) ? numValue : '';
      if (!isNaN(numValue)) {
        cell.numFmt = '0';
      }
    }
  });
});


    // Auto-adjust column widths
    columns.forEach((col, index) => {
      const width = col.label.length > 20 ? 25 : 18;
      worksheet.getColumn(index + 1).width = width;
    });

    // Generate and download
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    });

    const finalFilename = `MIS_Dist_Human_Loss_Report_${getTimestamp()}.xlsx`;
    saveAs(blob, finalFilename);
    console.log(`✅ MIS Excel downloaded: ${finalFilename}`);
  } catch (error) {
    console.error('❌ MIS Excel generation failed:', error);
    alert(`Excel Generation Error: ${error.message}`);
  }
};
